import {
  Button,
  Col,
  Row,
  Card,
  ButtonToolbar,
  ButtonGroup,
  Form,
} from 'react-bootstrap';
import { useMutation } from '@apollo/client';
import { useNavigate, useParams } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import { Form as FinalForm } from 'react-final-form';
import setFieldTouched from 'final-form-set-field-touched';
import { v4 as uuidv4 } from 'uuid';
import { Home as HomeIcon } from 'react-feather';

import cloneDeep from 'lodash.clonedeep';

import { toastSuccess, toastError } from '../lib/toast_helpers';
import { settingsSet } from '../store/settings_slice';

import LinkContainer from '../components/link_container';
import { renderOverlay, renderOffline } from '../components/render_helpers';
import SubmitButtonSpinner from '../components/submit_button_spinner';
import Field from '../components/form/rff_field';
import InputField from '../components/form/input_field';
import { coerceInput, handleSubmitError } from '../lib/utils';
import { userChangePasswordUpdate as userChangePasswordUpdateMutation } from '../graphql/user_queries';
import { userChangePasswordFormValidator } from '../validators';

const UserChangePasswordForm = () => {
  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.auth.user);
  const settingsTenant = useSelector((state) => state.settings.tenant);
  const settingsMutating = useSelector((state) => state.settings.mutating);
  const settingsOnline = useSelector((state) => state.settings.online);
  const [userChangePasswordUpdate] = useMutation(userChangePasswordUpdateMutation);

  const onCancel = () => {
    navigate('/');
  };

  const onFormSubmit = async (data) => {
    const uuid = uuidv4();
    const submitData = cloneDeep(data);
    const input = coerceInput(submitData);
    const mutationData = {
      variables: { input },
      context: {
        serializationKey: settingsTenant,
        tracked: true,
        recordType: 'UserType',
        recordId: params.id || uuid,
      },
    };
    mutationData.variables.id = currentUser.id;
    mutationData.context.mutationType = 'UPDATE';
    const mutation = userChangePasswordUpdate;
    // mutationData.update = updateFunctions.userUpdate;
    // mutationData.optimisticResponse = updateFunctions.optimisticNew({
    //   mutationName: 'userUpdate',
    //   mutationData,
    //   currentData: pageData.user,
    // });
    dispatch(
      settingsSet({
        mutating: true,
      })
    );
    if (settingsOnline) {
      try {
        await mutation(mutationData);
        toastSuccess(`User change password succeeded`);
        dispatch(
          settingsSet({
            mutating: false,
          })
        );
        navigate('/');
      } catch (err) {
        const { errorMessage, submitErrors } = handleSubmitError(err);
        dispatch(
          settingsSet({
            mutating: false,
          })
        );
        toastError(errorMessage);
        return submitErrors;
      }
    } else {
      toastError(`User Not Online. Go online to change your password`);
      dispatch(
        settingsSet({
          mutating: false,
        })
      );
      navigate('/');
    }
    return undefined;
  };

  const renderContent = () => (
    <>
      <Row className="mt-4 mb-3">
        <Col>
          <h1 className="h3 mb-3">Change Password</h1>
        </Col>
        <Col className="noprint" sm="auto">
          <ButtonToolbar>
            <ButtonGroup>
              <LinkContainer to="/">
                <Button variant="primary">
                  <HomeIcon />
                </Button>
              </LinkContainer>
            </ButtonGroup>
          </ButtonToolbar>
        </Col>
      </Row>
      <Row>
        <Col>
          <FinalForm
            initialValues={{}}
            onSubmit={(data) => onFormSubmit(data)}
            validate={userChangePasswordFormValidator}
            mutators={{ setFieldTouched }}
          >
            {({ handleSubmit, pristine, submitting }) => (
              <form noValidate>
                <Card>
                  <Card.Body>
                    <Field
                      name="currentPassword"
                      type="password"
                      component={InputField}
                      labelWidth={4}
                      inputWidth={8}
                      size="lg"
                    >
                      Current Password
                    </Field>
                    <Field
                      name="newPassword"
                      type="password"
                      component={InputField}
                      labelWidth={4}
                      inputWidth={8}
                      size="lg"
                    >
                      New Password
                    </Field>
                    <Field
                      name="newPasswordConfirmation"
                      type="password"
                      component={InputField}
                      labelWidth={4}
                      inputWidth={8}
                      size="lg"
                    >
                      New Password Confirmation
                    </Field>
                    <Form.Group as={Row}>
                      <Col sm={12}>
                        <ButtonToolbar style={{ justifyContent: 'flex-end' }}>
                          <ButtonGroup className="me-2">
                            <Button
                              variant="danger"
                              onClick={onCancel}
                              disabled={submitting}
                            >
                              Cancel
                            </Button>
                            <Button
                              type="button"
                              variant="primary"
                              disabled={pristine || submitting}
                              onClick={handleSubmit}
                            >
                              {submitting && <SubmitButtonSpinner />}
                              {params.id ? 'Update' : 'Create'}
                            </Button>
                          </ButtonGroup>
                        </ButtonToolbar>
                      </Col>
                    </Form.Group>
                  </Card.Body>
                </Card>
              </form>
            )}
          </FinalForm>
        </Col>
      </Row>
    </>
  );

  return (
    <div>
      {renderOverlay(false, settingsMutating, settingsOnline)}
      {renderOffline(settingsOnline)}
      {renderContent()}
    </div>
  );
};

export default UserChangePasswordForm;
